import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection, FeatureCard } from '../../components/footer/ContentSection';
import { CTASection } from '../../components/footer/CTASection';

export const CareersPage: React.FC = () => {
  const theme = useTheme();

  const benefits = [
    {
      icon: "🏥",
      title: "Health Insurance",
      description: "Comprehensive health, dental, and vision coverage for you and your family.",
    },
    {
      icon: "💰",
      title: "Competitive Salary",
      description: "Competitive compensation packages with performance bonuses and equity options.",
    },
    {
      icon: "🏖️",
      title: "Flexible Time Off",
      description: "Generous paid time off, sick leave, and flexible work arrangements.",
    },
    {
      icon: "📚",
      title: "Professional Development",
      description: "Learning and development opportunities, conference attendance, and training programs.",
    },
  ];

  const openPositions = [
    {
      title: "Software Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Product Manager",
      department: "Product",
      location: "San Francisco, CA",
      type: "Full-time",
    },
    {
      title: "Customer Success Manager",
      department: "Customer Success",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Marketing Specialist",
      department: "Marketing",
      location: "New York, NY",
      type: "Full-time",
    },
  ];

  return (
    <PageLayout
      title="Careers at Careva"
      subtitle="Join our team and help us revolutionize healthcare delivery. We're looking for passionate individuals who share our mission."
      breadcrumbs={[
        { label: "Company", href: "/company" },
        { label: "Careers" },
      ]}
    >
      {/* Company Culture */}
      <ContentSection
        title="Why Work at Careva?"
        subtitle="Join a mission-driven team that's making healthcare more accessible and improving patient outcomes."
        backgroundColor={theme.palette.grey[50]}
      >
        <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            At Careva, we're building the future of healthcare. We're looking for talented, passionate individuals 
            who want to make a real difference in people's lives. Join us in our mission to make quality healthcare 
            accessible to everyone.
          </Typography>
        </Box>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        title="Benefits & Perks"
        subtitle="We offer comprehensive benefits and perks to support our team members and their families."
      >
        <Grid container spacing={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    fontSize: '3rem',
                    mb: 2,
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {benefit.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* Open Positions */}
      <ContentSection
        title="Open Positions"
        subtitle="Explore current job openings and find the perfect role for you."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={3}>
          {openPositions.map((position, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  backgroundColor: 'white',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  {position.title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Department:</strong> {position.department}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Location:</strong> {position.location}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Type:</strong> {position.type}
                </Typography>
                <Button variant="outlined" size="small">
                  Apply Now
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Ready to Join Our Team?"
        subtitle="Don't see the perfect role? We're always looking for talented individuals. Send us your resume and let's talk."
        primaryButton={{
          text: "View All Positions",
          href: "/jobs",
        }}
        secondaryButton={{
          text: "Contact Us",
          href: "/contact",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
